import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Save, Calculator, Clock, Filter, Loader2, Layers, CalendarClock, Sparkles } from 'lucide-react';
import { FormulaBuilder } from '@/components/FormulaBuilder';
import { FormulaScopeHint } from '@/components/semantic-models/FormulaScopeHint';
import { BucketEditor } from '@/components/semantic-models/BucketEditor';
import { toast } from 'sonner';

// Scoped (conditional-aggregate) measure authoring — a base field aggregated
// over rows matching a set of WHERE clauses, e.g.
// SUM(POLICY_AMOUNT) WHERE AMOUNT_TYPE_CODE = 'premium'.
const SCOPE_OPS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '≠' },
  { value: 'in', label: 'in' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'like', label: 'like' },
  { value: 'between', label: 'between' },
];
const SCOPE_AGGS = ['sum', 'avg', 'count', 'count_distinct', 'min', 'max'];

// Catalogue of supported time-intel operators. `label` shows in the picker,
// `descTemplate` auto-fills the measure description so analysts don't have
// to type it. `formatHint` nudges the rendered output ("%" for yoy_pct so
// the chart axis adds the percent suffix without a manual unit override).
const TIME_INTEL_OPS = [
  {
    value: 'yoy_pct',
    label: 'Year-over-Year % change',
    icon: '📈',
    descTemplate: (of) => `Year-over-year percentage change in ${of}`,
    formatHint: 'percent',
  },
  {
    value: 'same_period_last_year',
    label: 'Same Period Last Year',
    icon: '🗓',
    descTemplate: (of) => `${of} for the same period in the prior year`,
    formatHint: '',
  },
  {
    value: 'prior_period',
    label: 'Prior Period',
    icon: '◀',
    descTemplate: (of) => `${of} from the immediately prior time period`,
    formatHint: '',
  },
  {
    value: 'ytd',
    label: 'Year-to-Date',
    icon: 'Σ',
    descTemplate: (of) => `Cumulative ${of} from January 1st (resets each calendar year)`,
    formatHint: '',
  },
  {
    value: 'qtd',
    label: 'Quarter-to-Date',
    icon: 'Q',
    descTemplate: (of) => `Quarter-to-date cumulative ${of} (resets each calendar quarter)`,
    formatHint: '',
  },
  {
    value: 'mtd',
    label: 'Month-to-Date',
    icon: 'M',
    descTemplate: (of) => `Month-to-date cumulative ${of} (resets the 1st of each month)`,
    formatHint: '',
  },
  {
    value: 'itd',
    label: 'Inception-to-Date',
    icon: '∞',
    descTemplate: (of) => `Inception-to-date cumulative ${of} (never resets)`,
    formatHint: '',
  },
];

/**
 * Edit Calculated Measure dialog — extracted from SemanticModelsPage.
 *
 * Supports TWO authoring modes:
 *   1. Formula        — the original AST-evaluator path.
 *   2. Time-intel     — a quick-pick that writes `time_intel: {op, of}` so
 *                       the query engine applies the operator post-aggregation
 *                       (prior_period / SPLY / yoy_pct / ytd).
 *
 * Mode is auto-selected when the existing measure has `time_intel` set; new
 * measures default to Formula. Toggling clears the other mode's fields so
 * we never PUT both `formula` and `time_intel` to the server.
 *
 * Auto-fires recompute on the model when the formula changes so dashboards
 * pick up the new math without a manual trigger.
 */
export function EditCalcMeasureDialog({
  open, onOpenChange,
  editCalcMeasure, setEditCalcMeasure,
  editCalcName, setEditCalcName,
  editCalcFormula, setEditCalcFormula,
  editCalcDesc, setEditCalcDesc,
  editCalcEntity, setEditCalcEntity,
  editCalcCursorPos, setEditCalcCursorPos,
  entities,
  measures = [],
  modelId,
  onAiGenerate,
  setCalcMeasures,
  activeModelId,
  onSave,
}) {
  // Mode + time-intel form state. Lives locally so toggling between modes
  // doesn't pollute the parent's formula state.
  const _detectMode = (m) => m?.time_intel
    ? 'time_intel'
    : ((m?.scope_filters?.length || (m?.base_field && !m?.formula)) ? 'scoped' : 'formula');
  const initialMode = _detectMode(editCalcMeasure);
  const [suggesting, setSuggesting] = useState(false);

  const suggestFromPql = async () => {
    const mid = modelId || activeModelId;
    if (!mid || !editCalcMeasure?.source_pql) return;
    setSuggesting(true);
    try {
      const res = await api.suggestFormulaFromPql({
        model_id: mid, source_pql: editCalcMeasure.source_pql, name: editCalcName || editCalcMeasure.name,
      });
      if (res.formula) {
        setEditCalcFormula(res.formula);
        if (res.valid) toast.success('AI² suggested a valid formula — review and save');
        else toast.message(res.error || 'Suggested a draft formula — please review and adjust');
      } else {
        toast.error(res.error || 'Could not suggest a formula');
      }
    } catch (e) { toast.error(e.message || 'Suggestion failed'); }
    finally { setSuggesting(false); }
  };

  const [mode, setMode] = useState(initialMode);
  const [tiOp, setTiOp] = useState(editCalcMeasure?.time_intel?.op || 'yoy_pct');
  const [tiOf, setTiOf] = useState(editCalcMeasure?.time_intel?.of || '');
  // Scoped (conditional-aggregate) local state.
  const [scAgg, setScAgg] = useState(editCalcMeasure?.aggregation || 'sum');
  const [scBase, setScBase] = useState(editCalcMeasure?.base_field || '');
  const [scFilters, setScFilters] = useState((editCalcMeasure?.scope_filters || []).map(f => ({ ...f })));
  // Point-in-time (snapshot-date) requirement — a scoped snapshot measure like
  // "Inforce Policy Count" that must be pinned to a single date.
  const [snapReq, setSnapReq] = useState(!!editCalcMeasure?.requires_snapshot_date);
  const [snapField, setSnapField] = useState(editCalcMeasure?.snapshot_date_field || '');
  const [snapMode, setSnapMode] = useState(editCalcMeasure?.snapshot_mode || 'as_of');
  const [bucketValid, setBucketValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folder, setFolder] = useState(editCalcMeasure?.display_folder || '');

  // Sync local state when a different measure is selected from the parent list.
  useEffect(() => {
    if (!editCalcMeasure) return;
    setMode(_detectMode(editCalcMeasure));
    setTiOp(editCalcMeasure.time_intel?.op || 'yoy_pct');
    setTiOf(editCalcMeasure.time_intel?.of || '');
    setScAgg(editCalcMeasure.aggregation || 'sum');
    setScBase(editCalcMeasure.base_field || '');
    setScFilters((editCalcMeasure.scope_filters || []).map(f => ({ ...f })));
    setSnapReq(!!editCalcMeasure.requires_snapshot_date);
    setSnapField(editCalcMeasure.snapshot_date_field || '');
    setSnapMode(editCalcMeasure.snapshot_mode || 'as_of');
    setFolder(editCalcMeasure.display_folder || '');
  }, [editCalcMeasure?.measure_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Field options for scoped authoring: prefer the measure's source entity,
  // else every attribute in the model.
  const scopeAttrs = useMemo(() => {
    const ent = entities.find(e => e.entity_id === editCalcEntity);
    return (ent?.attributes && ent.attributes.length) ? ent.attributes : entities.flatMap(e => e.attributes || []);
  }, [entities, editCalcEntity]);
  const updateScFilter = (i, patch) => setScFilters(prev => prev.map((f, x) => x === i ? { ...f, ...patch } : f));

  // Field option lists shared by the rich FormulaBuilder and the Bins & Groups editor.
  const allFields = useMemo(() => entities.flatMap(ent => (ent.attributes || []).map(a => ({ ...a, entityName: ent.name }))), [entities]);
  const numericFields = useMemo(() => allFields.filter(f => f.semantic_type === 'measure' || ['integer', 'float', 'number'].includes(f.data_type)), [allFields]);
  const dateFields = useMemo(() => allFields.filter(f => (f.data_type || '').toLowerCase().includes('date')), [allFields]);
  const textFields = useMemo(() => allFields.filter(f => ['string', 'text', 'varchar', 'character'].includes((f.data_type || '').toLowerCase())), [allFields]);
  const bucketEntity = useMemo(() => entities.find(e => e.entity_id === editCalcEntity) || entities[0], [entities, editCalcEntity]);

  // Build a flat list of "all numeric/measure-like fields in this model" for
  // the time-intel `of` picker. Includes both attribute measures from
  // entities AND other calc measures (so users can stack — e.g. YoY of a
  // calc measure like combined_ratio).
  const sourceMeasureOptions = useMemo(() => {
    const opts = [];
    entities.forEach(ent => {
      (ent.attributes || []).forEach(a => {
        if (a.semantic_type === 'measure') {
          opts.push({ value: a.name, label: a.description || a.name, group: ent.name });
        }
      });
    });
    // De-dupe by measure name — the same measure can appear on several entities
    // (e.g. loss_ratio on two facts). time_intel `of` only needs the name, and
    // Radix Select requires unique values/keys, so keep the first occurrence.
    const seen = new Set();
    return opts.filter(o => (seen.has(o.value) ? false : seen.add(o.value)));
  }, [entities]);

  const activeOp = TIME_INTEL_OPS.find(o => o.value === tiOp) || TIME_INTEL_OPS[0];

  // Auto-fill the description when the user picks an op + source — visible
  // in the Description field so they can edit before saving.
  useEffect(() => {
    if (mode !== 'time_intel' || !tiOf) return;
    const desc = activeOp.descTemplate(tiOf);
    if (!editCalcDesc || /^(Year-over-year|.* for the same period|.* from the immediately prior|Cumulative |Quarter-to-date|Month-to-date|Inception-to-date)/.test(editCalcDesc)) {
      setEditCalcDesc(desc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiOp, tiOf, mode]);

  const save = async () => {
    if (!editCalcMeasure || saving) return;
    const prevFormula = editCalcMeasure.formula || editCalcMeasure.expression || '';
    const isFormulaLike = mode === 'formula' || mode === 'buckets';
    const formulaChanged = isFormulaLike && prevFormula.trim() !== (editCalcFormula || '').trim();

    // Payload differs by mode — and we explicitly CLEAR the other mode's
    // field so a measure can't end up with both `formula` and `time_intel`.
    const payload = {
      name: editCalcName,
      description: editCalcDesc,
      source_entity_id: editCalcEntity,
      display_folder: (folder || '').trim(),
    };
    if (mode === 'time_intel') {
      if (!tiOf) { toast.error('Pick a source measure'); return; }
      payload.time_intel = { op: tiOp, of: tiOf };
      payload.formula = '';  // mutually exclusive
      if (activeOp.formatHint && !editCalcMeasure.format) {
        payload.format = activeOp.formatHint;
      }
    } else if (mode === 'scoped') {
      if (!scBase) { toast.error('Pick a base field'); return; }
      payload.base_field = scBase;
      payload.aggregation = scAgg;
      payload.scope_filters = scFilters
        .filter(f => f.field && (f.operator === 'between'
          ? (Array.isArray(f.value) && f.value[0] !== '' && f.value[1] !== '')
          : (f.value !== '' && f.value !== undefined && f.value !== null)))
        .map(f => ({ field: f.field, operator: f.operator || 'equals', value: f.value }));
      payload.formula = '';       // mutually exclusive
      payload.time_intel = null;
      // Point-in-time snapshot requirement (optional).
      payload.requires_snapshot_date = !!snapReq;
      if (snapReq) {
        if (!snapField) { toast.error('Pick the snapshot date field'); return; }
        payload.snapshot_date_field = snapField;
        payload.snapshot_mode = snapMode;
      } else {
        payload.snapshot_date_field = '';
      }
    } else {
      if (!editCalcFormula.trim()) { toast.error('Enter a formula'); return; }
      payload.formula = editCalcFormula;
      payload.time_intel = null;  // mutually exclusive
    }

    try {
      setSaving(true);
      // Certification flow (or any host) can override persistence: create the
      // measure in its model instead of the default in-place update.
      if (typeof onSave === 'function') {
        await onSave(payload);
        setEditCalcMeasure(null);
        onOpenChange(false);
        return;
      }
      const updated = await api.updateSemanticMeasure(editCalcMeasure.measure_id, payload);
      setCalcMeasures(prev => prev.map(m => m.measure_id === editCalcMeasure.measure_id ? updated : m));
      setEditCalcMeasure(null);
      onOpenChange(false);
      if (formulaChanged && activeModelId) {
        toast.success('Measure updated — recomputing…');
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/recompute-measures`, { method: 'POST', credentials: 'include' });
          if (!r.ok) throw new Error('recompute failed');
          const out = await r.json();
          if (out.measures_recomputed > 0) {
            toast.success(`Recomputed ${out.measures_recomputed} measure(s) × ${out.rows_updated} row(s)`);
          } else {
            toast.info(out.message || 'Saved (no row-level recompute needed)');
          }
        } catch {
          toast.error('Saved, but auto-recompute failed — use the Recompute button');
        }
      } else if (mode === 'time_intel') {
        toast.success('Time-intel measure saved — applied at query time, no recompute needed');
      } else {
        toast.success('Measure updated');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setEditCalcMeasure(null); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-heading">Edit Calculated Measure</DialogTitle></DialogHeader>
        {editCalcMeasure && (
          <>
          <div className="space-y-3 mt-1 flex-1 overflow-auto pr-1 min-h-0">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground">Measure Name</Label>
                <Input value={editCalcName} onChange={e => setEditCalcName(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Source Entity</Label>
                <Select value={editCalcEntity || '_none_'} onValueChange={v => setEditCalcEntity(v === '_none_' ? '' : v)}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">None</SelectItem>
                    {[...entities].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground">Display folder</Label>
              <Input value={folder} onChange={e => setFolder(e.target.value)} placeholder="e.g. Profitability — groups this measure in the field palette" className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="edit-measure-folder-input" />
            </div>

            {/* Mode toggle — drives whether we show the Formula editor or
                the Time-Intel quick-pick. Mutually exclusive on save. */}
            <div className="flex gap-1.5 p-0.5 rounded-lg bg-muted/30 border border-border/40" data-testid="calc-mode-toggle">
              <button
                onClick={() => setMode('formula')}
                type="button"
                className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-md transition-colors ${mode === 'formula' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid="calc-mode-formula"
              >
                <Calculator className="w-3 h-3" /> Formula
              </button>
              <button
                onClick={() => setMode('time_intel')}
                type="button"
                className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-md transition-colors ${mode === 'time_intel' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid="calc-mode-time-intel"
              >
                <Clock className="w-3 h-3" /> Time Intelligence
              </button>
              <button
                onClick={() => setMode('scoped')}
                type="button"
                className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-md transition-colors ${mode === 'scoped' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid="calc-mode-scoped"
              >
                <Filter className="w-3 h-3" /> Scoped
              </button>
              <button
                onClick={() => setMode('buckets')}
                type="button"
                className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-md transition-colors ${mode === 'buckets' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid="calc-mode-buckets"
              >
                <Layers className="w-3 h-3" /> Bins &amp; Groups
              </button>
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground">Description</Label>
              <Input value={editCalcDesc} onChange={e => setEditCalcDesc(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" />
            </div>

            {/* ── Time-intel mode UI ────────────────────────────────────── */}
            {mode === 'time_intel' && (
              <div className="space-y-3" data-testid="time-intel-fields">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Operator</Label>
                    <Select value={tiOp} onValueChange={setTiOp}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="ti-op-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIME_INTEL_OPS.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            <span className="inline-block w-4 text-center mr-1">{o.icon}</span>{o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Source Measure (of)</Label>
                    <Select value={tiOf || '_pick_'} onValueChange={v => setTiOf(v === '_pick_' ? '' : v)}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="ti-of-select">
                        <SelectValue placeholder="Pick a measure…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_pick_" disabled>Pick a measure…</SelectItem>
                        {sourceMeasureOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className="text-foreground">{opt.value}</span>
                            {opt.label !== opt.value && <span className="text-muted-foreground ml-2">— {opt.label}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20" data-testid="ti-preview">
                  <p className="text-[9px] uppercase tracking-wider text-emerald-400/80 font-semibold mb-1">Preview</p>
                  <p className="text-xs font-mono text-emerald-300">
                    {editCalcName || 'unnamed'} = <span className="text-cyan-400">{tiOp}</span>(<span className="text-amber-300">{tiOf || '?'}</span>)
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                    Applied at query time — needs a time dimension in the result. The source measure must also be selected in the query. No recompute step.
                  </p>
                </div>
              </div>
            )}

            {/* ── Scoped (conditional-aggregate) mode UI ─────────────────── */}
            {mode === 'scoped' && (
              <div className="space-y-3" data-testid="scoped-fields">
                {editCalcMeasure.is_auto_binding_measure && (
                  <p className="text-[10px] text-amber-300/90 bg-amber-500/5 border border-amber-500/20 rounded-md px-2 py-1.5">
                    Auto-generated from an aggregate binding — edits here may be overwritten when the aggregate refreshes.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Aggregation</Label>
                    <Select value={scAgg} onValueChange={setScAgg}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="scoped-agg-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{SCOPE_AGGS.map(a => <SelectItem key={a} value={a}>{a.toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Base Field</Label>
                    <Select value={scBase || '_pick_'} onValueChange={v => setScBase(v === '_pick_' ? '' : v)}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="scoped-base-select"><SelectValue placeholder="Pick a field…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_pick_" disabled>Pick a field…</SelectItem>
                        {scopeAttrs.map(a => <SelectItem key={a.name} value={a.name}><span className="font-mono">{a.name}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px] text-muted-foreground">Filters (WHERE — all must match)</Label>
                    <button type="button" onClick={() => setScFilters(prev => [...prev, { field: '', operator: 'equals', value: '' }])}
                      className="text-[10px] text-primary/80 hover:text-primary" data-testid="scoped-add-filter">+ condition</button>
                  </div>
                  <div className="space-y-1">
                    {scFilters.length === 0 && <p className="text-[10px] text-muted-foreground/80 italic">No filters — aggregates every row.</p>}
                    {scFilters.map((f, i) => (
                      <div key={i} className="flex items-center gap-1" data-testid={`scoped-filter-${i}`}>
                        <Select value={f.field || '_pick_'} onValueChange={v => updateScFilter(i, { field: v === '_pick_' ? '' : v })}>
                          <SelectTrigger className="h-7 flex-1 text-[11px] rounded-lg bg-muted/50 border-border/50"><SelectValue placeholder="field" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_pick_" disabled>field…</SelectItem>
                            {scopeAttrs.map(a => <SelectItem key={a.name} value={a.name} className="font-mono text-[11px]">{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={f.operator || 'equals'} onValueChange={v => updateScFilter(i, { operator: v, value: v === 'between' ? ['', ''] : (Array.isArray(f.value) ? '' : f.value) })}>
                          <SelectTrigger className="h-7 w-16 text-[11px] rounded-lg bg-muted/50 border-border/50"><SelectValue /></SelectTrigger>
                          <SelectContent>{SCOPE_OPS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {f.operator === 'between' ? (
                          <div className="flex items-center gap-1">
                            <Input value={Array.isArray(f.value) ? (f.value[0] ?? '') : ''} onChange={e => updateScFilter(i, { value: [e.target.value, Array.isArray(f.value) ? (f.value[1] ?? '') : ''] })} placeholder="from" className="h-7 w-16 text-[11px] rounded-lg bg-muted/50 border-border/50 font-mono" data-testid={`scoped-filter-value-${i}`} />
                            <span className="text-[10px] text-muted-foreground">·</span>
                            <Input value={Array.isArray(f.value) ? (f.value[1] ?? '') : ''} onChange={e => updateScFilter(i, { value: [Array.isArray(f.value) ? (f.value[0] ?? '') : '', e.target.value] })} placeholder="to" className="h-7 w-16 text-[11px] rounded-lg bg-muted/50 border-border/50 font-mono" data-testid={`scoped-filter-value2-${i}`} />
                          </div>
                        ) : (
                          <Input value={Array.isArray(f.value) ? '' : (f.value ?? '')} onChange={e => updateScFilter(i, { value: e.target.value })} placeholder={f.operator === 'like' ? '%pattern%' : 'value'}
                            className="h-7 w-28 text-[11px] rounded-lg bg-muted/50 border-border/50 font-mono" data-testid={`scoped-filter-value-${i}`} />
                        )}
                        <button type="button" onClick={() => setScFilters(prev => prev.filter((_, x) => x !== i))}
                          className="text-muted-foreground hover:text-destructive px-1 text-sm">×</button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20" data-testid="scoped-preview">
                  <p className="text-[9px] uppercase tracking-wider text-emerald-400/80 font-semibold mb-1">Preview</p>
                  <p className="text-xs font-mono text-emerald-300 break-words">
                    {editCalcName || 'unnamed'} = <span className="text-cyan-400">{scAgg.toUpperCase()}</span>(<span className="text-amber-300">{scBase || '?'}</span>)
                    {scFilters.filter(f => f.field).length > 0 && (
                      <> <span className="text-muted-foreground">WHERE</span> {scFilters.filter(f => f.field).map((f, i) => (
                        <span key={i}>{i > 0 && <span className="text-primary/80"> AND </span>}{f.field} {(SCOPE_OPS.find(o => o.value === (f.operator || 'equals')) || {}).label} {JSON.stringify(f.value)}</span>
                      ))}</>
                    )}
                  </p>
                </div>

                {/* Point-in-time snapshot-date requirement */}
                <div className="rounded-lg border border-border/50 bg-muted/20 p-3 space-y-2.5" data-testid="snapshot-req-section">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <CalendarClock className="w-3.5 h-3.5 mt-0.5 text-cyan-400 flex-shrink-0" />
                      <div>
                        <Label className="text-[11px] text-foreground">Requires a snapshot date</Label>
                        <p className="text-[10px] text-muted-foreground leading-snug">Point-in-time measure — pinned to one date so it never sums across periods.</p>
                      </div>
                    </div>
                    <Switch checked={snapReq} onCheckedChange={setSnapReq} data-testid="snapshot-req-toggle" />
                  </div>
                  {snapReq && (
                    <div className="grid grid-cols-2 gap-3 pt-0.5" data-testid="snapshot-req-fields">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Snapshot date field</Label>
                        <Select value={snapField || '_pick_'} onValueChange={v => setSnapField(v === '_pick_' ? '' : v)}>
                          <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm h-8" data-testid="snapshot-field-select"><SelectValue placeholder="Pick a date field…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_pick_" disabled>Pick a date field…</SelectItem>
                            {dateFields.map(a => <SelectItem key={a.name} value={a.name} className="font-mono text-[11px]">{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">When a date is chosen</Label>
                        <Select value={snapMode} onValueChange={setSnapMode}>
                          <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm h-8" data-testid="snapshot-mode-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">Exact date match</SelectItem>
                            <SelectItem value="as_of">As-of (latest on or before)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="col-span-2 text-[10px] text-muted-foreground leading-snug">
                        If no date is chosen, results auto-default to the latest available snapshot and are labelled with the date used.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Formula mode UI (rich editor — parity with Add dialog) ── */}
            {mode === 'formula' && (
              <>
                {editCalcMeasure?.source_pql && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-2" data-testid="pyramid-pql-assist">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400">Imported from Pyramid PQL</p>
                        <code className="block text-[10px] text-muted-foreground/80 break-all line-clamp-2 mt-0.5">{editCalcMeasure.source_pql}</code>
                      </div>
                      <Button type="button" size="sm" variant="outline" onClick={suggestFromPql} disabled={suggesting}
                        className="shrink-0 h-7 text-[11px] gap-1 border-amber-500/40 text-amber-600 hover:bg-amber-500/10" data-testid="suggest-formula-btn">
                        {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Suggest formula
                      </Button>
                    </div>
                  </div>
                )}
                <FormulaBuilder
                  formula={editCalcFormula}
                  onChange={setEditCalcFormula}
                  cursorPos={editCalcCursorPos}
                  onCursorChange={setEditCalcCursorPos}
                  fields={allFields}
                  measures={(measures || []).filter(m => !editCalcName || m.name !== editCalcName)}
                  onAiGenerate={onAiGenerate}
                  nameValue={editCalcName}
                  onTest={async (formula, name) => {
                    const mid = modelId || activeModelId;
                    if (!mid) return { ok: false, error: 'No active model' };
                    const comp = await api.testMeasureFormula(mid, { name: name || 'Test Measure', formula });
                    if (!comp.ok) return comp;
                    const dim = textFields[0] || dateFields[0] || allFields.find(f => f.semantic_type === 'dimension');
                    const dims = dim ? [dim.name] : [];
                    const col = name || 'Test Measure';
                    try {
                      const res = await api.executeQuery({ model_id: mid, dimensions: dims, measures: [col], calculations: [comp.spec], limit: 3 });
                      const rows = (res.data || []).slice(0, 3);
                      return { ok: true, rows, columns: dims.length ? [dims[0], col] : [col] };
                    } catch (e) {
                      return { ok: false, error: 'Compiled OK but failed to run: ' + (e?.message || e) };
                    }
                  }}
                />
                <FormulaScopeHint modelId={modelId || activeModelId} formula={editCalcFormula} />
              </>
            )}

            {/* ── Bins & Groups mode UI (parity with Add dialog) ────────── */}
            {mode === 'buckets' && (
              <div data-testid="edit-measure-buckets-builder">
                <BucketEditor
                  modelId={modelId || activeModelId}
                  entityName={bucketEntity?.name}
                  fields={allFields}
                  fieldsNumeric={numericFields}
                  fieldsDate={dateFields}
                  fieldsText={textFields}
                  testIdPrefix="edit-measure-bucket"
                  onChange={({ valid, formula: fx }) => {
                    setBucketValid(valid);
                    setEditCalcFormula(valid ? (fx || '') : '');
                  }}
                />
              </div>
            )}
          </div>

          {/* Sticky footer — always visible so you never scroll to reach Save. */}
          <div className="pt-2.5 mt-1 border-t border-border/40 flex-shrink-0">
            <Button
              onClick={save}
              disabled={saving || !editCalcName.trim() || (mode === 'formula' ? !editCalcFormula.trim() : mode === 'buckets' ? (!bucketValid || !editCalcFormula.trim()) : mode === 'time_intel' ? !tiOf : !scBase)}
              className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
              data-testid="save-calc-measure-btn"
            >
              {saving
                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>)
                : (<><Save className="w-4 h-4 mr-2" />Save Changes</>)}
            </Button>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
